// src/pages/HomePage.jsx (Hero Section Only - Updated with Public Folder Image)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import products from '../data/products';

const HomePage = () => {
  const navigate = useNavigate();
  const featuredProducts = products.filter(p => p.isNew || p.popular).slice(0, 8);
  const categories = [...new Set(products.map(p => p.category))];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);

  const categoryIcons = {
    'Flower': '🌿',
    'Exotic-Flower': '👑',
    'Edibles': '🍪',
    'Concentrates': '🔥',
    'cart': '💨',
    'disposable-cart': '⚡',
    'Pre-rolls': '📦',
    'Shake': '🍃'
  };

  const categoryColors = {
    'Flower': '#8b5cf6',
    'Exotic-Flower': '#ec489a',
    'Edibles': '#f59e0b',
    'Concentrates': '#ef4444',
    'cart': '#10b981',
    'disposable-cart': '#06b6d4',
    'Pre-rolls': '#a855f7',
    'Shake': '#6b7280'
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNavigation = useCallback((path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  const getSlidesPerView = useCallback(() => {
    if (windowWidth >= 1280) return 4;
    if (windowWidth >= 768) return 3;
    if (windowWidth >= 480) return 2;
    return 1;
  }, [windowWidth]);

  const slidesPerView = getSlidesPerView();
  const totalSlides = Math.ceil(categories.length / slidesPerView);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  useEffect(() => {
    if (isAutoPlaying && totalSlides > 1) {
      intervalRef.current = setInterval(nextSlide, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying, nextSlide, totalSlides]);

  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const handleMouseLeave = () => setIsAutoPlaying(true);

  const getCategoryStats = (category) => {
    const count = products.filter(p => p.category === category).length;
    const avgPrice = products
      .filter(p => p.category === category)
      .reduce((sum, p) => sum + (p.price || 0), 0) / count;
    return { count, avgPrice: Math.round(avgPrice) };
  };

  return (
    <div className="pot-castle-home">
      {/* Hero Section - Updated with Public Folder Image */}
      <section className="pc-hero">
        <div className="pc-hero-background">
          {/* Hero Image with Shadow - Using public folder image */}
          <div 
            className="pc-hero-image"
            style={{ backgroundImage: `url('/images/heros.png')` }}
          ></div>
          <div className="pc-hero-gradient"></div>
          <div className="pc-hero-pattern"></div>
        </div>
        
        <div className="pc-container">
          <div className="pc-hero-content">
            <div className="pc-hero-badge">
              <span className="pc-badge-icon">🏰</span>
              Washington DC's Premier Dispensary
            </div>
            
            <h1 className="pc-hero-title">
              Premium Cannabis
              <span className="pc-hero-title-accent">Delivered Fast</span>
            </h1>
            
            <p className="pc-hero-subtitle">
              Experience the finest selection of lab-tested, premium cannabis products.
              Discreet delivery right to your door in 30-45 minutes.
            </p>
            
            <div className="pc-hero-actions">
              <button
                onClick={() => handleNavigation('/products')}
                className="pc-btn pc-btn-primary pc-btn-lg"
              >
                <span>🛍️</span>
                Shop All Products
              </button>
              
              <button
                onClick={() => handleNavigation('/categories')}
                className="pc-btn pc-btn-outline pc-btn-lg"
              >
                <span>📍</span>
                Browse Categories
              </button>
            </div>

            <div className="pc-hero-stats">
              <div className="pc-stat">
                <span className="pc-stat-number">50+</span>
                <span className="pc-stat-label">Products</span>
              </div>
              <div className="pc-stat">
                <span className="pc-stat-number">30-45 min</span>
                <span className="pc-stat-label">Delivery</span>
              </div>
              <div className="pc-stat">
                <span className="pc-stat-number">500+</span>
                <span className="pc-stat-label">Happy Clients</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pc-hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" 
              fill="var(--pc-background)"/>
          </svg>
        </div>
      </section>

      {/* Rest of the component remains exactly the same */}
      {/* Featured Products */}
      <section className="pc-section">
        <div className="pc-container">
          <div className="pc-section-header">
            <div className="pc-section-header-content">
              <span className="pc-section-tag">Featured</span>
              <h2 className="pc-section-title">Popular Products</h2>
              <p className="pc-section-subtitle">Discover our most loved strains and edibles</p>
            </div>
            
            <button
              onClick={() => handleNavigation('/products')}
              className="pc-view-all"
            >
              <span>View All</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="pc-products-grid">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Category Carousel */}
      <section className="pc-section pc-section-carousel">
        <div className="pc-container">
          <div className="pc-section-header">
            <div className="pc-section-header-content">
              <span className="pc-section-tag">Shop by</span>
              <h2 className="pc-section-title">Categories</h2>
              <p className="pc-section-subtitle">Explore our curated collections</p>
            </div>

            {totalSlides > 1 && (
              <div className="pc-carousel-controls">
                <button
                  className="pc-carousel-btn pc-carousel-prev"
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                
                <button
                  className="pc-carousel-btn pc-carousel-next"
                  onClick={nextSlide}
                  disabled={currentSlide === totalSlides - 1}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div
            className="pc-carousel-container"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className="pc-carousel-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="pc-carousel-slide">
                  {categories
                    .slice(slideIndex * slidesPerView, (slideIndex + 1) * slidesPerView)
                    .map(category => {
                      const stats = getCategoryStats(category);
                      const icon = categoryIcons[category] || '🌿';
                      const color = categoryColors[category] || '#8b5cf6';

                      return (
                        <button
                          key={category}
                          onClick={() => handleNavigation(`/category/${category}`)}
                          className="pc-category-card"
                          style={{ '--category-color': color }}
                        >
                          <div className="pc-category-card-inner">
                            <div className="pc-category-front">
                              <div className="pc-category-icon">{icon}</div>
                              <h3 className="pc-category-name">
                                {category.replace('-', ' ')}
                              </h3>
                              <div className="pc-category-stats">
                                <span>{stats.count} Products</span>
                              </div>
                            </div>
                            
                            <div className="pc-category-back">
                              <span className="pc-category-explore">Explore</span>
                              <span className="pc-category-emoji">{icon}</span>
                              <span className="pc-category-price">From ${stats.avgPrice}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              ))}
            </div>
          </div>

          {totalSlides > 1 && (
            <div className="pc-carousel-dots">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`pc-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="pc-section pc-section-benefits">
        <div className="pc-container">
          <div className="pc-section-header pc-section-header-center">
            <span className="pc-section-tag">Why Choose Us</span>
            <h2 className="pc-section-title">The Pot Castle Difference</h2>
            <p className="pc-section-subtitle">Experience premium service with every order</p>
          </div>

          <div className="pc-benefits-grid">
            {[
              { icon: '⚡', title: 'Lightning Fast', desc: 'Delivery within 30-45 minutes across Washington DC', stats: ['45 min avg', 'DC Metro'] },
              { icon: '🔬', title: 'Lab Tested', desc: 'Every product tested for potency, purity, and safety', stats: ['Premium Quality', 'Top Rated'] },
              { icon: '🔒', title: 'Secure & Discreet', desc: 'Professional delivery with unmarked vehicles', stats: ['100% Secure', 'Discreet'] },
              { icon: '💬', title: 'Expert Support', desc: 'Knowledgeable team ready to help 24/7', stats: ['Certified', '24/7 Support'] }
            ].map((benefit, index) => (
              <div key={index} className="pc-benefit-card">
                <div className="pc-benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
                <div className="pc-benefit-stats">
                  {benefit.stats.map((stat, i) => (
                    <span key={i} className="pc-benefit-stat">{stat}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pc-cta">
        <div className="pc-container">
          <div className="pc-cta-content">
            <h2 className="pc-cta-title">Ready to Experience Premium Cannabis?</h2>
            <p className="pc-cta-text">Join thousands of satisfied customers in Washington DC</p>
            <div className="pc-cta-actions">
              <button
                onClick={() => handleNavigation('/products')}
                className="pc-btn pc-btn-light pc-btn-lg"
              >
                Start Shopping
              </button>
              <button
                onClick={() => handleNavigation('/about')}
                className="pc-btn pc-btn-outline-light pc-btn-lg"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        /* CSS Variables */
        :root {
          --pc-primary: #8b5cf6;
          --pc-primary-dark: #7c3aed;
          --pc-primary-light: #a78bfa;
          --pc-secondary: #ec489a;
          --pc-accent: #10b981;
          --pc-background: #ffffff;
          --pc-background-dark: #0a0a0a;
          --pc-surface: #ffffff;
          --pc-text: #18181b;
          --pc-text-light: #52525b;
          --pc-border: #e4e4e7;
          --pc-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          --pc-shadow-lg: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        @media (prefers-color-scheme: dark) {
          :root {
            --pc-background: #0a0a0a;
            --pc-surface: #171717;
            --pc-text: #fafafa;
            --pc-text-light: #a3a3a3;
            --pc-border: #262626;
          }
        }

        /* Base Styles */
        .pot-castle-home {
          background: var(--pc-background);
          color: var(--pc-text);
          overflow-x: hidden;
        }

        .pc-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 clamp(1rem, 5vw, 2rem);
        }

        /* Hero Section - Updated with Public Folder Image */
        .pc-hero {
          position: relative;
          min-height: 90vh;
          display: flex;
          align-items: center;
          overflow: hidden;
        }

        .pc-hero-background {
          position: absolute;
          inset: 0;
        }

        /* Hero Image with Shadow - Using public folder image */
        .pc-hero-image {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          filter: brightness(0.7);
          box-shadow: inset 0 0 100px rgba(0, 0, 0, 0.5), 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .pc-hero-gradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.8) 0%, rgba(236, 72, 153, 0.8) 100%);
        }

        .pc-hero-pattern {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 40px 40px;
        }

        .pc-hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          padding: clamp(3rem, 10vh, 6rem) 0;
        }

        .pc-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255,255,255,0.2);
          backdrop-filter: blur(10px);
          padding: 0.5rem 1.25rem;
          border-radius: 9999px;
          font-size: clamp(0.75rem, 2vw, 0.875rem);
          font-weight: 600;
          color: white;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(255,255,255,0.3);
        }

        .pc-hero-title {
          font-size: clamp(2rem, 8vw, 4.5rem);
          font-weight: 800;
          color: white;
          line-height: 1.1;
          margin-bottom: 1rem;
        }

        .pc-hero-title-accent {
          display: block;
          background: linear-gradient(90deg, #fbbf24, #f59e0b);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .pc-hero-subtitle {
          font-size: clamp(1rem, 3vw, 1.25rem);
          color: rgba(255,255,255,0.9);
          max-width: 600px;
          margin: 0 auto 2rem;
          line-height: 1.6;
        }

        .pc-hero-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 3rem;
        }

        .pc-hero-stats {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: clamp(1.5rem, 5vw, 3rem);
        }

        .pc-stat {
          text-align: center;
        }

        .pc-stat-number {
          display: block;
          font-size: clamp(1.5rem, 4vw, 2rem);
          font-weight: 700;
          color: #fbbf24;
        }

        .pc-stat-label {
          font-size: clamp(0.75rem, 1.5vw, 0.875rem);
          color: rgba(255,255,255,0.8);
        }

        .pc-hero-wave {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          line-height: 0;
        }

        .pc-hero-wave svg {
          width: 100%;
          height: clamp(40px, 8vw, 80px);
          color: var(--pc-background);
        }

        /* Buttons */
        .pc-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 0.75rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .pc-btn-lg {
          padding: 1rem 2rem;
          font-size: 1rem;
        }

        .pc-btn-primary {
          background: white;
          color: #8b5cf6;
        }

        .pc-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.2);
        }

        .pc-btn-outline {
          background: transparent;
          border: 2px solid white;
          color: white;
        }

        .pc-btn-outline:hover {
          background: white;
          color: #8b5cf6;
          transform: translateY(-2px);
        }

        .pc-btn-light {
          background: white;
          color: #8b5cf6;
        }

        .pc-btn-outline-light {
          background: transparent;
          border: 2px solid white;
          color: white;
        }

        .pc-btn-outline-light:hover {
          background: white;
          color: #8b5cf6;
        }

        /* Section Styles */
        .pc-section {
          padding: clamp(3rem, 8vh, 5rem) 0;
        }

        .pc-section-header {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: clamp(2rem, 5vh, 3rem);
          gap: 1.5rem;
        }

        .pc-section-header-center {
          text-align: center;
          justify-content: center;
        }

        .pc-section-header-content {
          flex: 1;
        }

        .pc-section-tag {
          display: inline-block;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #8b5cf6;
          margin-bottom: 0.5rem;
        }

        .pc-section-title {
          font-size: clamp(1.8rem, 5vw, 2.5rem);
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .pc-section-subtitle {
          font-size: clamp(0.9rem, 2vw, 1rem);
          color: var(--pc-text-light);
        }

        /* Products Grid */
        .pc-products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
          gap: clamp(1rem, 3vw, 2rem);
        }

        /* View All Button */
        .pc-view-all {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: transparent;
          border: 2px solid #8b5cf6;
          border-radius: 0.75rem;
          color: #8b5cf6;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .pc-view-all:hover {
          background: #8b5cf6;
          color: white;
          transform: translateY(-2px);
        }

        /* Category Carousel */
        .pc-section-carousel {
          background: var(--pc-background);
        }

        .pc-carousel-container {
          overflow: hidden;
          margin: 0 -0.5rem;
        }

        .pc-carousel-track {
          display: flex;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .pc-carousel-slide {
          flex: 0 0 100%;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
          gap: clamp(1rem, 2.5vw, 1.5rem);
          padding: 0 0.5rem;
        }

        .pc-carousel-controls {
          display: flex;
          gap: 0.75rem;
        }

        .pc-carousel-btn {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: var(--pc-surface);
          border: 1px solid var(--pc-border);
          color: var(--pc-text);
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pc-carousel-btn:hover:not(:disabled) {
          background: #8b5cf6;
          border-color: #8b5cf6;
          color: white;
          transform: scale(1.05);
        }

        .pc-carousel-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pc-carousel-btn svg {
          width: 20px;
          height: 20px;
        }

        .pc-carousel-dots {
          display: flex;
          justify-content: center;
          gap: 0.75rem;
          margin-top: 2rem;
        }

        .pc-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--pc-border);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .pc-dot.active {
          width: 24px;
          background: #8b5cf6;
        }

        /* Category Cards */
        .pc-category-card {
          position: relative;
          width: 100%;
          aspect-ratio: 1;
          perspective: 1000px;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
        }

        .pc-category-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.6s;
          transform-style: preserve-3d;
          border-radius: 1rem;
        }

        .pc-category-card:hover .pc-category-card-inner {
          transform: rotateY(180deg);
        }

        .pc-category-front,
        .pc-category-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          border-radius: 1rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1rem;
        }

        .pc-category-front {
          background: var(--pc-surface);
          border: 1px solid var(--pc-border);
          box-shadow: var(--pc-shadow);
        }

        .pc-category-back {
          background: linear-gradient(135deg, #8b5cf6, #ec489a);
          color: white;
          transform: rotateY(180deg);
        }

        .pc-category-icon {
          font-size: clamp(2rem, 6vw, 3rem);
        }

        .pc-category-name {
          font-size: clamp(1rem, 2.5vw, 1.125rem);
          font-weight: 700;
          text-transform: capitalize;
        }

        .pc-category-stats {
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          color: var(--pc-text-light);
        }

        .pc-category-back .pc-category-stats {
          color: rgba(255,255,255,0.8);
        }

        .pc-category-explore {
          font-size: clamp(0.8rem, 2vw, 0.9rem);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .pc-category-emoji {
          font-size: clamp(1.5rem, 4vw, 2rem);
        }

        .pc-category-price {
          font-size: clamp(0.75rem, 1.5vw, 0.85rem);
          opacity: 0.9;
        }

        /* Benefits Section */
        .pc-section-benefits {
          background: var(--pc-background);
        }

        .pc-benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
          gap: clamp(1.5rem, 3vw, 2rem);
          margin-top: 2rem;
        }

        .pc-benefit-card {
          background: var(--pc-surface);
          border-radius: 1rem;
          padding: clamp(1.5rem, 4vw, 2rem);
          text-align: center;
          border: 1px solid var(--pc-border);
          transition: all 0.3s ease;
        }

        .pc-benefit-card:hover {
          transform: translateY(-8px);
          box-shadow: var(--pc-shadow-lg);
          border-color: #8b5cf6;
        }

        .pc-benefit-icon {
          font-size: clamp(2.5rem, 6vw, 3rem);
          margin-bottom: 1rem;
        }

        .pc-benefit-card h3 {
          font-size: clamp(1.1rem, 2.5vw, 1.25rem);
          font-weight: 700;
          margin-bottom: 0.75rem;
        }

        .pc-benefit-card p {
          color: var(--pc-text-light);
          line-height: 1.6;
          margin-bottom: 1rem;
          font-size: clamp(0.85rem, 1.8vw, 0.95rem);
        }

        .pc-benefit-stats {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
        }

        .pc-benefit-stat {
          font-size: clamp(0.7rem, 1.5vw, 0.75rem);
          padding: 0.25rem 0.75rem;
          background: rgba(139, 92, 246, 0.1);
          border-radius: 9999px;
          color: #8b5cf6;
        }

        /* CTA Section */
        .pc-cta {
          background: linear-gradient(135deg, #8b5cf6, #ec489a);
          padding: clamp(3rem, 8vh, 5rem) 0;
          text-align: center;
        }

        .pc-cta-content {
          max-width: 700px;
          margin: 0 auto;
        }

        .pc-cta-title {
          font-size: clamp(1.5rem, 5vw, 2.5rem);
          font-weight: 700;
          color: white;
          margin-bottom: 1rem;
        }

        .pc-cta-text {
          font-size: clamp(1rem, 2.5vw, 1.125rem);
          color: rgba(255,255,255,0.9);
          margin-bottom: 2rem;
        }

        .pc-cta-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: center;
        }

        /* Responsive Breakpoints */
        @media (max-width: 768px) {
          .pc-section-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .pc-view-all {
            width: 100%;
            justify-content: center;
          }
          
          .pc-carousel-controls {
            align-self: flex-end;
          }
          
          .pc-carousel-slide {
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
          }
          
          .pc-benefits-grid {
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
          }
        }

        @media (max-width: 480px) {
          .pc-hero-actions {
            flex-direction: column;
            width: 100%;
          }
          
          .pc-hero-actions .pc-btn {
            width: 100%;
            justify-content: center;
          }
          
          .pc-carousel-slide {
            grid-template-columns: 1fr;
          }
          
          .pc-benefits-grid {
            grid-template-columns: 1fr;
          }
          
          .pc-cta-actions {
            flex-direction: column;
          }
          
          .pc-cta-actions .pc-btn {
            width: 100%;
          }
        }

        /* Touch Device Optimizations */
        @media (hover: none) {
          .pc-category-card:hover .pc-category-card-inner {
            transform: none;
          }
          
          .pc-benefit-card:hover {
            transform: none;
          }
          
          .pc-btn:active {
            transform: scale(0.98);
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;